import { ProtocolWorkEffects } from './features/protocol-work/state/protocol-work.effects';
import { protocolWorkFeature } from './features/protocol-work/state/protocol-work.state';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  withNavigationErrorHandler,
  withPreloading,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { Store, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { QuicklinkStrategy } from 'ngx-quicklink';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideToast } from 'ngx-mt-ui-lib/services/toast';
import { PageNotFoundConfig } from 'ngx-mt-ui-lib/components/page-not-found';
import { NoAccessConfig } from 'ngx-mt-ui-lib/components/no-access';
import packageJson from '../../package.json';
import { provideLibTranslations, ru } from 'ngx-mt-ui-lib/translate';
import { authStateInitializer } from '@core/app-initializers/auth-state.initializer';
import { authReducer } from './features/auth/auth.reducer';
import { publicReducer } from './features/public/public.reducer';
import { AuthEffects } from './features/auth/auth.effects';
import { PublicEffects } from './features/public/public.effects';
import { publicStateInitializer } from '@core/app-initializers/public-state.initializer';
import { tokenInterceptor } from '@core/interceptors/token.interceptor';
import { AccessService } from 'mt-ui-lib/services/access';
import { AscupAccessService } from './core/services/ascup-access.service';
import {
  CHAT_API_CONFIG,
  CHAT_CONVERSATION_LINK_ADAPTER,
  CHAT_CONVERSATION_PRESENTATION_ADAPTER,
  CHAT_ENTITY_LINK_ADAPTER,
  CHAT_REFERENCE_SUGGESTION_SOURCE,
  ChatApiConfig,
  isChatApiAuthenticationFailure,
  provideMtChatClient,
} from 'ias-ppr-mt-chat-client';
import { AscupChatConversationAdapter } from './features/chat-list/services/ascup-chat-conversation-presentation.adapter';
import { AscupChatConversationLinkAdapter } from './features/chat-list/services/ascup-chat-conversation-link.adapter';
import { AscupChatEntityLinkAdapter } from './features/chat-list/services/ascup-chat-entity-link.adapter';
import { AscupChatReferenceSuggestionSource } from './features/chat-list/services/ascup-chat-reference-suggestion.source';
import { IAppState } from './app.state';
import { selectCurrentUser } from './features/auth/auth.selectors';
import { environment } from '@env/environment';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { KeycloakSessionService } from '@core/services/auth/keycloak-session.service';
import {
  cacheInvalidateInterceptor,
  cacheInterceptor,
  CacheService,
  InMemoryCacheService,
  provideCacheContext,
  ECacheTTL,
} from 'ngx-mt-ui-lib/cache';
import { translateInitializer } from './core/app-initializers/translate.initializer';
import { ToastConfig } from 'ngx-mt-ui-lib/services/toast';
import { LucideInfo, LucideTriangleAlert } from '@lucide/angular';
import {
  EventLogApiService,
  provideApiClient,
  provideReactiveApi,
} from 'ngx-mt-ui-lib/api-client';
import { AscupReactiveApiConfig } from './core/reactive-api/ascup-reactive-api.config';
import { EVENT_LOG_API } from '@app/shared/event-log/event-log-api.token';
import { ChunkLoadRecoveryService } from '@core/services/chunk-load-recovery.service';

export const appToastConfig: Partial<ToastConfig> = {
  duration: 2000,
  icon: { icon: LucideInfo },
  summaryDisplay: 'wrap',
  error: {
    duration: 3000,
    icon: { icon: LucideTriangleAlert, color: 'var(--md-sys-color-error)' },
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(QuicklinkStrategy),
      withNavigationErrorHandler((error) =>
        inject(ChunkLoadRecoveryService).recover(error),
      ),
    ),
    provideStore({
      protocolWork: protocolWorkFeature.reducer,
      auth: authReducer,
      public: publicReducer,
    }),
    provideEffects(AuthEffects, PublicEffects, ProtocolWorkEffects),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    provideHttpClient(
      withInterceptors([
        tokenInterceptor,
        cacheInvalidateInterceptor,
        cacheInterceptor,
      ]),
    ),
    { provide: CacheService, useClass: InMemoryCacheService },
    provideApiClient({ apiBasePath: environment.API_URL }),
    { provide: EVENT_LOG_API, useExisting: EventLogApiService },
    provideReactiveApi(AscupReactiveApiConfig),
    provideCacheContext({
      cacheable: false,
      ttl: ECacheTTL.MINUTE * 5,
    }),
    provideAnimationsAsync(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: isDevMode() ? '.json' : `.json?v=${packageJson.version}`,
        enforceLoading: isDevMode(),
      }),
      fallbackLang: 'ru',
    }),
    provideLibTranslations({
      ru: ru,
      //en: en,
    }),
    provideToast(appToastConfig),
    {
      provide: PageNotFoundConfig,
      useValue: <PageNotFoundConfig>{
        mainUrl: '/',
      },
    },
    {
      provide: NoAccessConfig,
      useValue: <NoAccessConfig>{
        mainUrl: '/',
      },
    },
    {
      provide: AccessService,
      useClass: AscupAccessService,
    },
    provideMtChatClient({ browserUnreadIndicator: true }),
    {
      provide: CHAT_API_CONFIG,
      deps: [Store, KeycloakSessionService],
      useFactory: (
        store: Store<IAppState>,
        session: KeycloakSessionService,
      ): ChatApiConfig => {
        const session$ = combineLatest([
          session.state$,
          store.select(selectCurrentUser),
        ]).pipe(
          switchMap(([state, currentUser]) =>
            state.authenticated && currentUser
              ? from(session.accessToken()).pipe(
                  map((accessToken) => ({
                    accessToken,
                    identityKey: session.subject,
                  })),
                  catchError(() => of(null)),
                )
              : of(null),
          ),
          distinctUntilChanged(
            (previous, current) =>
              previous?.accessToken === current?.accessToken &&
              previous?.identityKey === current?.identityKey,
          ),
          shareReplay({ bufferSize: 1, refCount: true }),
        );

        return {
          apiPath: environment.CHAT_API,
          realtimePath: environment.CHAT_WS,
          session: () => session$,
          isAuthenticationFailure: isChatApiAuthenticationFailure,
          authenticationFailed: ({ accessToken, reason }) => {
            if (reason === 'reauthenticationRequired') {
              session.rejectSession(accessToken);
              return;
            }

            void session.accessToken(accessToken).catch(() => undefined);
          },
        };
      },
    },
    {
      provide: CHAT_CONVERSATION_PRESENTATION_ADAPTER,
      useClass: AscupChatConversationAdapter,
    },
    {
      provide: CHAT_CONVERSATION_LINK_ADAPTER,
      useClass: AscupChatConversationLinkAdapter,
    },
    {
      provide: CHAT_ENTITY_LINK_ADAPTER,
      useClass: AscupChatEntityLinkAdapter,
    },
    {
      provide: CHAT_REFERENCE_SUGGESTION_SOURCE,
      useClass: AscupChatReferenceSuggestionSource,
    },
    // INITIALIZERS
    provideAppInitializer(authStateInitializer),
    provideAppInitializer(publicStateInitializer),
    provideAppInitializer(translateInitializer),
  ],
};
